import { z } from "zod";
import { PHOTO_KINDS, ROLES, SHOPS, STATES } from "./enums.js";
import { formConfigSchema } from "./form-config.js";
import { staffPhone, usZip } from "./job.js";

// People, dealerships and job photos: the admin side of the directory.

/**
 * One person, one role. Reps carry their 2-3 dealerships and their manager; a dealership login
 * carries exactly one dealership; managers and admins carry none.
 */
export const personInputSchema = z
  .object({
    name: z.string().trim().min(2).max(80),
    email: z.email().transform((e) => e.toLowerCase()),
    phone: staffPhone.optional(),
    role: z.enum(ROLES),
    storeIds: z.array(z.string().min(1)).max(20).default([]),
    managerId: z.string().min(1).optional(),
    active: z.boolean().default(true),
  })
  .superRefine((p, ctx) => {
    if (p.role === "sales_rep" && p.storeIds.length === 0) {
      ctx.addIssue({ code: "custom", path: ["storeIds"], message: "Pick at least one dealership" });
    }
    if (p.role === "dealership" && p.storeIds.length !== 1) {
      ctx.addIssue({ code: "custom", path: ["storeIds"], message: "A dealership login has exactly one dealership" });
    }
    if (p.role !== "sales_rep" && p.managerId) {
      ctx.addIssue({ code: "custom", path: ["managerId"], message: "Only reps have a manager" });
    }
  });
export type PersonInput = z.input<typeof personInputSchema>;

export const personSchema = z.object({
  userId: z.string(),
  name: z.string(),
  email: z.string(),
  phone: z.string().optional(),
  role: z.enum(ROLES),
  storeIds: z.array(z.string()),
  managerId: z.string().optional(),
  active: z.boolean(),
});
export type Person = z.infer<typeof personSchema>;
export const peopleResponseSchema = z.object({ items: z.array(personSchema) });

export const storeInputSchema = z.object({
  name: z.string().trim().min(2).max(100),
  groupId: z.string().regex(/^grp_[a-z0-9_]+$/, "Use grp_ and lowercase letters, e.g. grp_sun_auto").optional(),
  shop: z.enum(SHOPS),
  state: z.enum(STATES),
  city: z.string().trim().max(60).optional(),
  zip: usZip.optional(),
  timeZone: z.string().default("America/New_York"),
  active: z.boolean().default(true),
  formConfig: formConfigSchema.optional(),
});
export type StoreInput = z.input<typeof storeInputSchema>;

export const adminStoreSchema = z.object({
  storeId: z.string(),
  name: z.string(),
  groupId: z.string().optional(),
  shop: z.enum(SHOPS),
  state: z.enum(STATES).optional(),
  city: z.string().optional(),
  zip: z.string().optional(),
  timeZone: z.string(),
  active: z.boolean(),
  formConfig: formConfigSchema.optional(),
});
export type AdminStore = z.infer<typeof adminStoreSchema>;
export const storesResponseSchema = z.object({ items: z.array(adminStoreSchema) });

export const jobPhotosResponseSchema = z.object({
  items: z.array(z.object({ photoId: z.string(), kind: z.enum(PHOTO_KINDS), url: z.url(), thumbUrl: z.url().optional() })),
});
export type JobPhoto = z.infer<typeof jobPhotosResponseSchema>["items"][number];

/** A sales manager's whole team in one call: these reps report to them, no others. */
export const teamInputSchema = z.object({ repIds: z.array(z.string().min(1)).max(200) });
export type TeamInput = z.input<typeof teamInputSchema>;

// ---- Change log: who changed which person or dealership, when, and from what to what ----

export const AUDIT_ENTITIES = ["person", "store"] as const;
export type AuditEntity = (typeof AUDIT_ENTITIES)[number];

export const auditChangeSchema = z.object({ field: z.string(), before: z.unknown().optional(), after: z.unknown().optional() });
export const auditEntrySchema = z.object({
  auditId: z.string(),
  at: z.iso.datetime(),
  actor: z.object({ userId: z.string(), name: z.string() }),
  entity: z.enum(AUDIT_ENTITIES),
  entityId: z.string(),
  /** Name at the time, so the log reads well after a rename. */
  entityName: z.string(),
  action: z.enum(["created", "updated"]),
  changes: z.array(auditChangeSchema),
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;

/** Newest first. With entityId: one person's or dealership's history (entity required). */
export const auditQuerySchema = z
  .object({
    entity: z.enum(AUDIT_ENTITIES).optional(),
    entityId: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
  })
  .refine((q) => !q.entityId || q.entity, { path: ["entity"], message: "Say whether entityId is a person or a store" });
export type AuditQuery = z.output<typeof auditQuerySchema>;
export const auditResponseSchema = z.object({ items: z.array(auditEntrySchema) });

// ---- Bulk add people from a CSV (admin) ----

export const PERSON_IMPORT_MAX = 500;

/**
 * One CSV row as typed. The server resolves roles ("Rep", "Manager", "Dealership", "Admin"), dealerships by
 * name or id (several split by ";" or "|"), and the manager by email, including a manager added in the same file.
 */
export const personImportRowSchema = z.object({
  line: z.number().int().min(1),
  name: z.string().max(200).default(""),
  email: z.string().max(200).default(""),
  phone: z.string().max(40).optional(),
  role: z.string().max(40).default(""),
  dealerships: z.string().max(2000).optional(),
  managerEmail: z.string().max(200).optional(),
});
export type PersonImportRow = z.input<typeof personImportRowSchema>;

/** apply false: check every row and change nothing (the preview). apply true: add the good rows. */
export const personImportSchema = z.object({
  rows: z.array(personImportRowSchema).min(1).max(PERSON_IMPORT_MAX),
  apply: z.boolean().default(false),
});

export const PERSON_IMPORT_OUTCOMES = ["add", "skip", "error"] as const;
export const personImportResultSchema = z.object({
  applied: z.boolean(),
  counts: z.object({ add: z.number(), skip: z.number(), error: z.number() }),
  rows: z.array(z.object({
    line: z.number(),
    name: z.string(),
    email: z.string(),
    outcome: z.enum(PERSON_IMPORT_OUTCOMES),
    message: z.string().optional(),
    userId: z.string().optional(),
  })),
});
export type PersonImportResult = z.infer<typeof personImportResultSchema>;

// ---- Served ZIP codes (admin) ----

export const SERVED_ZIP_MAX = 20_000;

/**
 * replace: the list becomes exactly these ZIPs. add / remove: change the current list.
 * apply false: show what would change and save nothing.
 */
export const servedZipsUpdateSchema = z.object({
  zips: z.array(z.string().max(12)).max(SERVED_ZIP_MAX),
  mode: z.enum(["replace", "add", "remove"]),
  apply: z.boolean().default(false),
});
export type ServedZipsUpdate = z.input<typeof servedZipsUpdateSchema>;

export const servedZipsResponseSchema = z.object({
  zips: z.array(z.string()),
  /** built_in: the list shipped in code (served-zips.ts), until an admin saves one. */
  source: z.enum(["admin", "built_in"]),
  updatedAt: z.iso.datetime().optional(),
  updatedBy: z.string().optional(),
});
export type ServedZipsResponse = z.infer<typeof servedZipsResponseSchema>;

export const servedZipsChangeSchema = z.object({
  applied: z.boolean(),
  count: z.number(),
  added: z.array(z.string()),
  removed: z.array(z.string()),
  /** Not a 5-digit ZIP in FL, SC or NC. */
  rejected: z.array(z.string()),
});
export type ServedZipsChange = z.infer<typeof servedZipsChangeSchema>;
